import { isEmpty } from 'lodash';
import { toastConfig } from 'config';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import SectionCard from 'components/section/SectionCard';
import { Col, Form, Input, Row, Select, Spin } from 'antd';
import AddIconButton from 'components/button/AddIconButton';
import { useLocation, useNavigate } from 'react-router-dom';
import { filterChildOptionLabel } from 'pages/globals/global';
import AntdValuePreview from 'components/dev/AntdValuePreview';
import CustomTextInput from 'components/inputs/CustomTextInput';
import CustomDateInput from 'components/inputs/CustomDateInput';
import SolutionCatalogIcon from 'assets/icons/solutions-catalog';
import DetailPageHeader from 'components/header/DetailPageHeader';
import DeleteIconButton from 'components/button/DeleteIconButton';
import FormSubmitButton from 'components/button/FormSubmitButton';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import { useGetLynFormDataMutation } from 'store/features/comptroller/comptrollerApi';
import { useGetLynFormPropertyMutation, useGetSingleIdentityMutation } from 'store/features/process/processApi';
import { Box, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';

export const queryNameMap = {
	'PROPERTY_Identity-Opt_band': 'Bands',
	'PROPERTY_Identity-Opt_position': 'leavePolicyPositions',
	'PROPERTY_Identity-Opt_function': 'leavePolicyFunctions',
	'PROPERTY_Identity-Opt_userId': 'leavePolicyUsers',
};

function Create() {
	const [form] = Form.useForm();

	const { state } = useLocation();
	const navigate = useNavigate();

	const [submitData, setSubmitData] = useState();
	const [showModal, setShowModal] = useState(false);

	const [GetSingleIdentity, { error: errorData, isSuccess: isSingleCaseSuccess, isLoading: isSingleCaseLoading, isError: isSingleCaseError }] =
		useGetSingleIdentityMutation();

	const onSubmitHandler = (payload) => {
		setSubmitData(payload);
		setShowModal(true);
	};

	const onModalOkHandler = () => {
		GetSingleIdentity({
			operation: 'add-single',
			processCode: state?.processCode,
			suffixCode: '',
			data: submitData,
		});
	};

	useEffect(() => {
		if (isSingleCaseSuccess) {
			toast.success('Success! Action completed ', toastConfig);
			navigate(-1);
		}

		if (isSingleCaseError) {
			if (errorData?.data?.code === '501') {
				toast.error(errorData?.data?.message, toastConfig);
			} else {
				toast.error('Failed! Action not completed', toastConfig);
			}
		}
	}, [isSingleCaseSuccess, isSingleCaseError, errorData]);

	useEffect(() => {
		if (isEmpty(state?.processCode)) {
			navigate(-1);
		}
	}, [state?.processCode]);

	return (
		<Box>
			<DetailPageHeader
				title={'Create ' + (state?.processName ?? '')}
				image={<SolutionCatalogIcon color="#fff" width="20px" height="20px" />}
			/>

			<Spin spinning={isSingleCaseLoading} style={{ maxHeight: '100%' }}>
				<SectionCard>
					<Form
						form={form}
						id="leave-policy-create"
						layout="vertical"
						initialValues={{
							annualPolicy: [null],
							sickPolicy: [null],
							casualPolicy: [null],
						}}
						onFinish={onSubmitHandler}
						autoComplete="off"
					>
						<AntdValuePreview form={form} show={false} />

						<Row gutter={[16, 0]}>
							<Col xs={24}>
								<CustomTextInput
									form={form}
									name="policyName"
									label="Leave Policy Name"
									placeholder="Enter leave policy name"
									rules={[
										{
											required: true,
											message: 'Leave policy name field is required',
										},
									]}
								/>
							</Col>

							<Col xs={24}>
								<CustomDateInput
									form={form}
									name="date"
									label="Effective Date"
									rules={[
										{
											required: true,
											message: 'Effective date field is required',
										},
									]}
								/>
							</Col>

							<Col xs={24}>
								<Typography sx={{ fontSize: '14px', fontWeight: '600', mb: '4px' }}>Annual Policy</Typography>

								<Form.List name="annualPolicy">
									{(fields, { add, remove }) => (
										<>
											<Box>
												<TableContainer
													sx={{
														mb: 3,
														'& table, & th, & td': {
															border: '1px solid #e0e0e0',
															borderCollapse: 'collapse',
														},
														'& th': {
															py: '5px',
															px: '11px',
														},
														'& td': {
															p: '5px',
														},
														'& td .ant-form-item': {
															marginBottom: '0px',
														},
														'& .condition-msg': {
															py: 3,
														},
													}}
												>
													<Table>
														<TableHead>
															<TableRow sx={{ backgroundColor: '#e5e5e5' }}>
																<TableCell sx={{ minWidth: '10rem' }}>Identity</TableCell>
																<TableCell sx={{ minWidth: '10rem' }}>Operand</TableCell>
																<TableCell sx={{ minWidth: '10rem' }}>Value</TableCell>
																<TableCell sx={{ minWidth: '10rem' }}>Entitlement in Days</TableCell>
																<TableCell sx={{ minWidth: '10rem' }}>Leave Accrual</TableCell>
																<TableCell sx={{ minWidth: '10rem' }}>Probation Period</TableCell>
																<TableCell sx={{ minWidth: '10rem' }}>Max. Carryover Days</TableCell>
																<TableCell sx={{ minWidth: '15rem' }}>Max. Annual Encashment</TableCell>
																<TableCell sx={{ minWidth: '10rem' }}>Advance Leave</TableCell>
																<TableCell sx={{ minWidth: '6rem', textAlign: 'center' }}>Action</TableCell>
															</TableRow>
														</TableHead>
														<TableBody>
															{fields?.map((field, index) => (
																<TableRow key={index}>
																	<TableCell sx={{ verticalAlign: 'sub', width: '10rem' }}>
																		<PolicyTableSelect
																			name={[field.name, 'PROPERTY_Identity-Opt']}
																			displayNameCode={[field.name, 'identityLabel']}
																			code="PROPERTY_Identity-Opt"
																		/>
																	</TableCell>

																	<TableCell sx={{ verticalAlign: 'sub', width: '10rem' }}>
																		<PolicyTableSelect
																			name={[field.name, 'PROPERTY_Operand-Opt']}
																			displayNameCode={[field.name, 'operandLabel']}
																			code="PROPERTY_Operand-Opt"
																		/>
																	</TableCell>

																	<TableCell sx={{ verticalAlign: 'sub', width: '10rem' }}>
																		<Form.Item
																			noStyle
																			shouldUpdate={(prevValues, currentValues) =>
																				prevValues?.annualPolicy?.[index]?.['PROPERTY_Identity-Opt'] !==
																				currentValues?.annualPolicy?.[index]?.['PROPERTY_Identity-Opt']
																			}
																		>
																			{({ getFieldValue }) => (
																				<PolicyTableSelect
																					isDependent={true}
																					name={[field.name, 'value']}
																					displayNameCode={[field.name, 'valueLabel']}
																					code={
																						queryNameMap?.[
																							getFieldValue([
																								'annualPolicy',
																								index,
																								'PROPERTY_Identity-Opt',
																							])
																						] ?? null
																					}
																				/>
																			)}
																		</Form.Item>
																	</TableCell>

																	<TableCell sx={{ verticalAlign: 'sub', minWidth: '10rem' }}>
																		<Form.Item style={{ margin: 0 }} name={[field.name, 'entitlementDays']}>
																			<Input placeholder="Write here" autoComplete="off" />
																		</Form.Item>
																	</TableCell>

																	<TableCell sx={{ verticalAlign: 'sub', minWidth: '10rem' }}>
																		<Form.Item style={{ margin: 0 }} name={[field.name, 'leaveAccrual']}>
																			<Select
																				allowClear
																				style={{ width: '10rem' }}
																				showSearch
																				filterOption={filterChildOptionLabel}
																				placeholder="Select from the list"
																				autoComplete="off"
																			>
																				<Select.Option value="monthly">Monthly</Select.Option>
																				<Select.Option value="yearly">Yearly</Select.Option>
																				<Select.Option value="quarterly">Quarterly</Select.Option>
																			</Select>
																		</Form.Item>
																	</TableCell>

																	<TableCell sx={{ verticalAlign: 'sub', minWidth: '10rem' }}>
																		<Form.Item style={{ margin: 0 }} name={[field.name, 'probationPeriod']}>
																			<Select
																				allowClear
																				style={{ width: '10rem' }}
																				showSearch
																				filterOption={filterChildOptionLabel}
																				placeholder="Select from the list"
																				autoComplete="off"
																			>
																				<Select.Option value="accrued">Accrued</Select.Option>
																				<Select.Option value="notAccrued">Not accrued</Select.Option>
																			</Select>
																		</Form.Item>
																	</TableCell>

																	<TableCell sx={{ verticalAlign: 'sub', minWidth: '10rem' }}>
																		<Form.Item style={{ margin: 0 }} name={[field.name, 'carryoverDays']}>
																			<Input placeholder="Write here" autoComplete="off" />
																		</Form.Item>
																	</TableCell>

																	<TableCell sx={{ verticalAlign: 'sub', minWidth: '10rem' }}>
																		<Form.Item style={{ margin: 0 }} name={[field.name, 'annualEncashment']}>
																			<Input placeholder="Write here" autoComplete="off" />
																		</Form.Item>
																	</TableCell>

																	<TableCell sx={{ verticalAlign: 'sub', minWidth: '10rem' }}>
																		<Form.Item style={{ margin: 0 }} name={[field.name, 'advanceLeave']}>
																			<Select
																				allowClear
																				style={{ width: '10rem' }}
																				showSearch
																				filterOption={filterChildOptionLabel}
																				placeholder="Select from the list"
																				autoComplete="off"
																			>
																				<Select.Option value="allowed">Allowed</Select.Option>
																				<Select.Option value="notAllowed">Not Allowed</Select.Option>
																			</Select>
																		</Form.Item>
																	</TableCell>

																	<TableCell sx={{ textAlign: 'center' }}>
																		<AddIconButton onClick={() => add()} />

																		{fields.length > 1 && index > 0 && (
																			<DeleteIconButton onClick={() => remove(index)} />
																		)}
																	</TableCell>
																</TableRow>
															))}
														</TableBody>
													</Table>
												</TableContainer>
											</Box>
										</>
									)}
								</Form.List>
							</Col>

							<Col xs={24}>
								<Typography sx={{ fontSize: '14px', fontWeight: '600', mb: '4px' }}>Sick Policy</Typography>

								<Form.List name="sickPolicy">
									{(fields, { add, remove }) => (
										<>
											<Box>
												<TableContainer
													sx={{
														mb: 3,
														'& table, & th, & td': {
															border: '1px solid #e0e0e0',
															borderCollapse: 'collapse',
														},
														'& th': {
															py: '5px',
															px: '11px',
														},
														'& td': {
															p: '5px',
														},
														'& td .ant-form-item': {
															marginBottom: '0px',
														},
														'& .condition-msg': {
															py: 3,
														},
													}}
												>
													<Table>
														<TableHead>
															<TableRow sx={{ backgroundColor: '#e5e5e5' }}>
																<TableCell sx={{ minWidth: '10rem' }}>Identity</TableCell>
																<TableCell sx={{ minWidth: '10rem' }}>Operand</TableCell>
																<TableCell sx={{ minWidth: '10rem' }}>Value</TableCell>
																<TableCell sx={{ minWidth: '10rem' }}>Entitlement in Days</TableCell>
																<TableCell sx={{ minWidth: '10rem' }}>Leave Accrual</TableCell>
																<TableCell sx={{ minWidth: '10rem' }}>Probation Period</TableCell>
																<TableCell sx={{ minWidth: '6rem', textAlign: 'center' }}>Action</TableCell>
															</TableRow>
														</TableHead>

														<TableBody>
															{fields?.map((field, index) => (
																<TableRow key={index}>
																	<TableCell sx={{ verticalAlign: 'sub', width: '10rem' }}>
																		<PolicyTableSelect
																			name={[field.name, 'PROPERTY_Identity-Opt']}
																			displayNameCode={[field.name, 'identityLabel']}
																			code="PROPERTY_Identity-Opt"
																		/>
																	</TableCell>

																	<TableCell sx={{ verticalAlign: 'sub', width: '10rem' }}>
																		<PolicyTableSelect
																			name={[field.name, 'PROPERTY_Operand-Opt']}
																			displayNameCode={[field.name, 'operandLabel']}
																			code="PROPERTY_Operand-Opt"
																		/>
																	</TableCell>

																	<TableCell sx={{ verticalAlign: 'sub', width: '10rem' }}>
																		<Form.Item
																			noStyle
																			shouldUpdate={(prevValues, currentValues) =>
																				prevValues?.sickPolicy?.[index]?.['PROPERTY_Identity-Opt'] !==
																				currentValues?.sickPolicy?.[index]?.['PROPERTY_Identity-Opt']
																			}
																		>
																			{({ getFieldValue }) => (
																				<PolicyTableSelect
																					isDependent={true}
																					name={[field.name, 'value']}
																					displayNameCode={[field.name, 'valueLabel']}
																					code={
																						queryNameMap?.[
																							getFieldValue([
																								'sickPolicy',
																								index,
																								'PROPERTY_Identity-Opt',
																							])
																						] ?? null
																					}
																				/>
																			)}
																		</Form.Item>
																	</TableCell>

																	<TableCell sx={{ verticalAlign: 'sub', minWidth: '10rem' }}>
																		<Form.Item style={{ margin: 0 }} name={[field.name, 'entitlementDays']}>
																			<Input placeholder="Write here" autoComplete="off" />
																		</Form.Item>
																	</TableCell>

																	<TableCell sx={{ verticalAlign: 'sub', minWidth: '10rem' }}>
																		<Form.Item style={{ margin: 0 }} name={[field.name, 'leaveAccrual']}>
																			<Select
																				allowClear
																				style={{ width: '100%' }}
																				showSearch
																				filterOption={filterChildOptionLabel}
																				placeholder="Select from the list"
																				autoComplete="off"
																			>
																				<Select.Option value="monthly">Monthly</Select.Option>
																				<Select.Option value="yearly">Yearly</Select.Option>
																				<Select.Option value="quarterly">Quarterly</Select.Option>
																			</Select>
																		</Form.Item>
																	</TableCell>

																	<TableCell sx={{ verticalAlign: 'sub', minWidth: '10rem' }}>
																		<Form.Item style={{ margin: 0 }} name={[field.name, 'probationPeriod']}>
																			<Select
																				allowClear
																				style={{ width: '100%' }}
																				showSearch
																				filterOption={filterChildOptionLabel}
																				placeholder="Select from the list"
																				autoComplete="off"
																			>
																				<Select.Option value="accrued">Accrued</Select.Option>
																				<Select.Option value="notAccrued">Not accrued</Select.Option>
																			</Select>
																		</Form.Item>
																	</TableCell>

																	<TableCell sx={{ textAlign: 'center' }}>
																		<AddIconButton onClick={() => add()} />

																		{fields.length > 1 && index > 0 && (
																			<DeleteIconButton onClick={() => remove(index)} />
																		)}
																	</TableCell>
																</TableRow>
															))}
														</TableBody>
													</Table>
												</TableContainer>
											</Box>
										</>
									)}
								</Form.List>
							</Col>

							<Col xs={24}>
								<Typography sx={{ fontSize: '14px', fontWeight: '600', mb: '4px' }}>Casual Policy</Typography>

								<Form.List name="casualPolicy">
									{(fields, { add, remove }) => (
										<>
											<Box>
												<TableContainer
													sx={{
														mb: 3,
														'& table, & th, & td': {
															border: '1px solid #e0e0e0',
															borderCollapse: 'collapse',
														},
														'& th': {
															py: '5px',
															px: '11px',
														},
														'& td': {
															p: '5px',
														},
														'& td .ant-form-item': {
															marginBottom: '0px',
														},
														'& .condition-msg': {
															py: 3,
														},
													}}
												>
													<Table>
														<TableHead>
															<TableRow sx={{ backgroundColor: '#e5e5e5' }}>
																<TableCell sx={{ minWidth: '10rem' }}>Identity</TableCell>
																<TableCell sx={{ minWidth: '10rem' }}>Operand</TableCell>
																<TableCell sx={{ minWidth: '10rem' }}>Value</TableCell>
																<TableCell sx={{ minWidth: '10rem' }}>Entitlement in Days</TableCell>
																<TableCell sx={{ minWidth: '10rem' }}>Leave Accrual</TableCell>
																<TableCell sx={{ minWidth: '10rem' }}>Probation Period</TableCell>
																<TableCell sx={{ minWidth: '6rem', textAlign: 'center' }}>Action</TableCell>
															</TableRow>
														</TableHead>

														<TableBody>
															{fields?.map((field, index) => (
																<TableRow key={index}>
																	<TableCell sx={{ verticalAlign: 'sub', width: '10rem' }}>
																		<PolicyTableSelect
																			name={[field.name, 'PROPERTY_Identity-Opt']}
																			displayNameCode={[field.name, 'identityLabel']}
																			code="PROPERTY_Identity-Opt"
																		/>
																	</TableCell>

																	<TableCell sx={{ verticalAlign: 'sub', width: '10rem' }}>
																		<PolicyTableSelect
																			name={[field.name, 'PROPERTY_Operand-Opt']}
																			displayNameCode={[field.name, 'operandLabel']}
																			code="PROPERTY_Operand-Opt"
																		/>
																	</TableCell>

																	<TableCell sx={{ verticalAlign: 'sub', width: '10rem' }}>
																		<Form.Item
																			noStyle
																			shouldUpdate={(prevValues, currentValues) =>
																				prevValues?.casualPolicy?.[index]?.['PROPERTY_Identity-Opt'] !==
																				currentValues?.casualPolicy?.[index]?.['PROPERTY_Identity-Opt']
																			}
																		>
																			{({ getFieldValue }) => (
																				<PolicyTableSelect
																					isDependent={true}
																					name={[field.name, 'value']}
																					displayNameCode={[field.name, 'valueLabel']}
																					code={
																						queryNameMap?.[
																							getFieldValue([
																								'casualPolicy',
																								index,
																								'PROPERTY_Identity-Opt',
																							])
																						] ?? null
																					}
																				/>
																			)}
																		</Form.Item>
																	</TableCell>

																	<TableCell sx={{ verticalAlign: 'sub', minWidth: '10rem' }}>
																		<Form.Item style={{ margin: 0 }} name={[field.name, 'entitlementDays']}>
																			<Input placeholder="Write here" autoComplete="off" />
																		</Form.Item>
																	</TableCell>

																	<TableCell sx={{ verticalAlign: 'sub', minWidth: '10rem' }}>
																		<Form.Item style={{ margin: 0 }} name={[field.name, 'leaveAccrual']}>
																			<Select
																				allowClear
																				style={{ width: '100%' }}
																				showSearch
																				filterOption={filterChildOptionLabel}
																				placeholder="Select from the list"
																				autoComplete="off"
																			>
																				<Select.Option value="monthly">Monthly</Select.Option>
																				<Select.Option value="yearly">Yearly</Select.Option>
																				<Select.Option value="quarterly">Quarterly</Select.Option>
																			</Select>
																		</Form.Item>
																	</TableCell>

																	<TableCell sx={{ verticalAlign: 'sub', minWidth: '10rem' }}>
																		<Form.Item style={{ margin: 0 }} name={[field.name, 'probationPeriod']}>
																			<Select
																				allowClear
																				style={{ width: '100%' }}
																				showSearch
																				filterOption={filterChildOptionLabel}
																				placeholder="Select from the list"
																				autoComplete="off"
																			>
																				<Select.Option value="accrued">Accrued</Select.Option>
																				<Select.Option value="notAccrued">Not accrued</Select.Option>
																			</Select>
																		</Form.Item>
																	</TableCell>

																	<TableCell sx={{ textAlign: 'center' }}>
																		<AddIconButton onClick={() => add()} />

																		{fields.length > 1 && index > 0 && (
																			<DeleteIconButton onClick={() => remove(index)} />
																		)}
																	</TableCell>
																</TableRow>
															))}
														</TableBody>
													</Table>
												</TableContainer>
											</Box>
										</>
									)}
								</Form.List>
							</Col>

							<Col xs={24}>
								<FormSubmitButton />
							</Col>
						</Row>
					</Form>
				</SectionCard>
			</Spin>

			{showModal && (
				<ComptrollerConfirmModal
					showModal={showModal}
					setShowModal={setShowModal}
					handleSubmit={onModalOkHandler}
					modalData={{
						head: 'Leave Policy',
						label: 'Do you want to proceed?',
					}}
				/>
			)}
		</Box>
	);
}

export default Create;

export const PolicyTableSelect = ({ name, displayNameCode, code, rules, dependentCode, dp, isDependent = false }) => {
	const form = Form.useFormInstance();
	const userId = useSelector((state) => state.profile.profileData.userId);
	const companyCode = useSelector((state) => state.profile.profileData.creatorOrganizationId);
	const { state } = useLocation();

	const [options, setOptions] = useState(isDependent ? [] : null);

	const [GetLynFormData, { data: lynFormData }] = useGetLynFormDataMutation();
	const [GetLynFormProperty, { data: lynProperty }] = useGetLynFormPropertyMutation();

	const onSelectHandler = (value) => {
		if (displayNameCode) {
			const label = form.getFieldValue(value);
			form.setFieldValue(['policy', ...displayNameCode], label);
		}
	};

	const onChangeHandler = () => {
		if (dependentCode) {
			form.setFieldValue(dependentCode);
		}
	};

	useEffect(() => {
		if (code?.startsWith('PROPERTY_')) {
			GetLynFormProperty({
				type: 'FORM',
				fieldCode: state?.processCode || dp?.processCode,
				processVersion: state?.processVersion || dp?.processVersion,
				propertyCode: code,
				companyCode,
				status: 'Active',
			});
		} else if (code && userId) {
			GetLynFormData({
				name: code,
				userId,
			});
		}
	}, [code, userId]);

	useEffect(() => {
		if (lynProperty?.lynProperties?.length > 0) {
			setOptions(lynProperty?.lynProperties ?? []);
			lynProperty?.lynProperties?.forEach((value) => {
				form.setFieldsValue({
					[value?.propertyValue]: value?.displayName,
				});
			});
		}

		if (lynFormData?.results?.length > 0) {
			setOptions(lynFormData?.results ?? []);
			lynFormData?.results?.forEach((value) => {
				form.setFieldsValue({
					[value?.propertyValue]: value?.displayName,
				});
			});
		}
	}, [lynProperty, lynFormData]);

	return (
		<>
			<Form.Item name={name} style={{ margin: 0 }} rules={rules}>
				{options === null ? (
					<Skeleton variant="text" sx={{ fontSize: '1rem', marginX: 1 }} />
				) : (
					<Select
						allowClear
						style={{ width: '100%' }}
						showSearch
						filterOption={filterChildOptionLabel}
						onChange={onChangeHandler}
						onSelect={onSelectHandler}
						placeholder="Select from the list"
						autoComplete="off"
					>
						{options?.map((value, index) => (
							<Select.Option key={index} value={value?.propertyValue}>
								{value?.displayName}
							</Select.Option>
						))}
					</Select>
				)}
			</Form.Item>
			<Form.Item noStyle name={displayNameCode} />
		</>
	);
};
